import { cva, type VariantProps } from 'class-variance-authority';
import { useState } from 'react';
import { mergeStyling } from '../common-functions';
import OdinIcon, { OdinIconSize } from '../odin-icon/odin-icon.component';

const userAvatarVariants = cva('shrink-0 rounded-full', {
  variants: {
    size: {
      sm: 'size-6',
      default: 'size-8',
      lg: 'size-12',
      xl: 'size-16',
    },
  },
  defaultVariants: {
    size: 'default',
  },
});

const iconSizeMap = {
  sm: OdinIconSize.ExtraSmall,
  default: OdinIconSize.Small,
  lg: OdinIconSize.Medium,
  xl: OdinIconSize.Large,
} as const;

export type UserAvatarProps = VariantProps<typeof userAvatarVariants> & {
  /** Full name used for the alt text. */
  name: string;
  /** Image URL. When absent, a generic user icon is shown instead. */
  src?: string;
  className?: string;
};

export const UserAvatar = ({ name, src, size, className }: UserAvatarProps) => {
  const [hasImageError, setHasImageError] = useState(false);
  const baseClasses = mergeStyling(userAvatarVariants({ size }), className);
  const showImage = src && !hasImageError;

  return (
    <div
      className={mergeStyling(
        baseClasses,
        'relative flex items-center justify-center overflow-hidden bg-border text-muted-foreground'
      )}
    >
      <OdinIcon icon="User" size={iconSizeMap[size ?? 'default']} aria-hidden />
      {showImage && (
        <img
          src={src}
          alt={name}
          onError={() => setHasImageError(true)}
          className="absolute inset-0 h-full w-full object-cover"
        />
      )}
    </div>
  );
};
